import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';

import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { Observable, Subject, firstValueFrom, forkJoin, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, filter, take } from 'rxjs/operators';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { CardModule } from 'primeng/card';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { DividerModule } from 'primeng/divider';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessageModule } from 'primeng/message';
import { TableModule } from 'primeng/table';

// Models and services
import {
  CommissionPlanRuleResponse,
  CreateCommissionPlanRuleRequest,
  UpdateCommissionPlanRuleRequest,
  CommissionRuleCategory,
  CommissionSalesBasis,
  CommissionScopeType,
  CommissionRateType,
  CreateCommissionPlanSlabRequest,
  CommissionSlabDimensionValue,
  OwnershipLevel
} from '../../models';
import { CommissionPlanRuleService, CommissionPlanService, CommissionPlanSlabService } from '../../services';
import {
  ToastMessageService,
  SubscriptionService,
  ServiceCategoryService,
  CommonService,
  PlanmasterService
} from '@fitness-force/api';

import { ProductService, ProductSkuService } from '@fitness-force/retail-billing';
import { SERVICETYPE_LIST_STATE, TENANTS_INFO } from '@fitness-force/state';
import type { ServiceTypeResponse, TENANTSINTERFACE } from '@fitness-force/models';
import { ServiceBehaviour, Status } from '@fitness-force/enum';
import { BreadcrumbComponent } from '@fitness-force/common/breadcrumb';
import { CheckboxModule } from 'primeng/checkbox';

interface SlabRow {
  slabId?: number;           // present for rows that already exist in the API
  quantity_from: number | null;
  quantity_to: number | null;
  // Dynamic column values keyed by column name
  [key: string]: number | null | string | undefined;
}

@Component({
  selector: 'lib-commission-plan-rules-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    MultiSelectModule,
    CardModule,
    RadioButtonModule,
    ToggleSwitchModule,
    DividerModule,
    InputNumberModule,
    MessageModule,
    BreadcrumbComponent,
    CheckboxModule,
    TableModule
  ],
  templateUrl: './commission-plan-rules-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommissionPlanRulesFormComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly commissionPlanRuleService = inject(CommissionPlanRuleService);
  private readonly commissionPlanSlabService = inject(CommissionPlanSlabService);
  private readonly commissionPlanService = inject(CommissionPlanService);
  private readonly toastService = inject(ToastMessageService);
  private readonly translate = inject(TranslateService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject(Store);
  private readonly subscriptionService = inject(SubscriptionService);
  private readonly serviceCategoryService = inject(ServiceCategoryService);
  private readonly commonService = inject(CommonService);
  private readonly planmasterService = inject(PlanmasterService);
  private readonly productService = inject(ProductService);
  private readonly productSkuService = inject(ProductSkuService);

  private readonly destroy$ = new Subject<void>();

  ruleForm: FormGroup;
  saving = false;
  planId!: number;
  ruleId?: number;
  rule?: CommissionPlanRuleResponse;
  selectedOwnershipLevel = OwnershipLevel.Company;
  tenantId = 0;
  tenantFranchiseId = 0;
  tenantsList: any[] = [];
  currencyCode = 'USD';
  locale = 'en-US';

  // Rate type toggle: true = percentage, false = flat fee
  isPercentageRate = true;

  // Slab table data
  slabRows: SlabRow[] = [];
  slabColumns: string[] = [];
  frozenColumns = [
    { field: 'quantity_from', header: 'Quantity From' },
    { field: 'quantity_to', header: 'Quantity To' }
  ];

  // Data arrays
  subscriptionPlans: { id: number; name: string }[] = [];
  serviceCategories: { id: number; name: string; behaviour: ServiceBehaviour }[] = [];
  retailProducts: { id: number; name: string }[] = [];
  productSkus: { sku_name: string; name: string; product_name: string; product_id: number }[] = [];

  // Loading states
  loadingPlans = false;
  loadingServices = false;
  loadingProducts = false;
  loadingSkus = false;

  // Selected product IDs for SKU fetching
  selectedProductIds: number[] = [];

  /** Avoid redundant HTTP when ownership context is unchanged */
  private lastSubscriptionPlansFetchKey: string | null = null;
  private lastServiceCategoriesFetchKey: string | null = null;

  private readonly skuCache: Map<number, { sku_name: string; name: string; product_name: string; product_id: number }[]> = new Map();
  private isLoadingSkus = false;

  // Sales Based is hidden in UI but kept for future use
  categoryOptions = [
    { label: 'Appointment Usage Based', value: CommissionRuleCategory.AppointmentUsageBased },
    { label: 'Class Usage Based', value: CommissionRuleCategory.ClassUsageBased },
    { label: 'Sales Based', value: CommissionRuleCategory.SalesBased, hidden: true }
  ];

  get scopeTypeOptions() {
    return [
      { label: 'All', value: CommissionScopeType.All },
      { label: 'Subscription Plan', value: CommissionScopeType.SubscriptionPlan },
      { label: 'Service Category', value: CommissionScopeType.ServiceCategory },
      { label: 'Retail Item', value: CommissionScopeType.RetailItem }
    ];
  }

  get scopeTypeEnum() {
    return CommissionScopeType;
  }

  get isEditMode(): boolean {
    return !!this.ruleId;
  }

  get isCategorySelected(): boolean {
    return !!this.ruleForm.get('category')?.value;
  }

  get filteredServiceCategories(): { id: number; name: string; behaviour: ServiceBehaviour }[] {
    const category = this.ruleForm?.get('category')?.value;
    if (category === CommissionRuleCategory.AppointmentUsageBased) {
      return this.serviceCategories.filter(s =>
        s.behaviour === ServiceBehaviour.AppointmentPackage ||
        String(s.behaviour).toLowerCase() === 'appointmentpackage' ||
        String(s.behaviour) === String(ServiceBehaviour.AppointmentPackage)
      );
    }
    if (category === CommissionRuleCategory.ClassUsageBased) {
      return this.serviceCategories.filter(s =>
        s.behaviour === ServiceBehaviour.ClassPackage ||
        String(s.behaviour).toLowerCase() === 'classpackage' ||
        String(s.behaviour) === String(ServiceBehaviour.ClassPackage)
      );
    }
    return this.serviceCategories;
  }

  get isScopeValid(): boolean {
    const scopeType = this.ruleForm.get('scope_type')?.value;
    if (!scopeType) return false;
    if (scopeType === CommissionScopeType.All) return true;
    if (scopeType === CommissionScopeType.SubscriptionPlan) {
      const plans = this.ruleForm.get('scope_plans')?.value;
      return plans && plans.length > 0;
    } else if (scopeType === CommissionScopeType.ServiceCategory) {
      const services = this.ruleForm.get('scope_services')?.value;
      return services && services.length > 0;
    } else if (scopeType === CommissionScopeType.RetailItem) {
      const products = this.ruleForm.get('scope_products')?.value;
      return products && products.length > 0;
    }
    return false;
  }

  constructor() {
    this.ruleForm = this.fb.group({
      category: [null, Validators.required],
      sales_basis: [null], // hidden, reserved for future sales_based category use
      scope_type: [{ value: null, disabled: true }, Validators.required],
      scope_plans: [[]],
      scope_services: [[]],
      scope_products: [[]],
      scope_skus: [[]],
      limit_specific_skus: [false],
      flat_trial_value: [null],
      flat_free_value: [null],
      flat_comp_value: [null],
      noshow_value_in_percent: [null]
    });
  }

  ngOnInit() {
    const currencyInfo = this.commonService.getCurrencyCodeAndLocale();
    this.currencyCode = currencyInfo.currencyCode || 'USD';
    this.locale = currencyInfo.locale || 'en-US';
    this.changeDetectorRef.detectChanges();

    // Restore ownership from the :ownership path segment so a page refresh works correctly.
    const ownershipParam = this.route.snapshot.paramMap.get('ownership') as OwnershipLevel | null;
    if (ownershipParam && Object.values(OwnershipLevel).includes(ownershipParam)) {
      this.selectedOwnershipLevel = ownershipParam;
      this.commissionPlanService.setSelectedOwnershipLevel(ownershipParam);
    } else {
      this.selectedOwnershipLevel = this.commissionPlanService.getSelectedOwnershipLevel();
    }

    this.store.select(TENANTS_INFO)
      .pipe(
        filter((t: TENANTSINTERFACE) => !!t?.selected_tenant?.id),
        takeUntil(this.destroy$)
      )
      .subscribe((tenant: TENANTSINTERFACE) => {
        this.tenantId = tenant.selected_tenant.id;
        this.tenantFranchiseId = tenant.selected_tenant.franchise_group_id;
        this.tenantsList = tenant.all_tenant || [];
      });

    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.planId = +params['planId'];
        if (params['ruleId']) {
          this.ruleId = +params['ruleId'];
          this.loadRule();
        }
      });

    this.ruleForm.get('scope_products')?.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntil(this.destroy$)
      )
      .subscribe((productIds: number[]) => {
        this.selectedProductIds = productIds || [];
        if (this.selectedProductIds.length > 0 && this.ruleForm.get('limit_specific_skus')?.value) {
          this.loadProductSkus();
        } else {
          this.productSkus = [];
          this.ruleForm.patchValue({ scope_skus: [] });
        }
        this.changeDetectorRef.detectChanges();
      });
  }

  onCategoryChange() {
    const category = this.ruleForm.get('category')?.value;
    if (category === CommissionRuleCategory.AppointmentUsageBased || category === CommissionRuleCategory.ClassUsageBased) {
      // session_count is the default for usage-based categories
      this.ruleForm.patchValue({ sales_basis: CommissionSalesBasis.SessionCount });
      this.ruleForm.get('sales_basis')?.clearValidators();
    } else if (category === CommissionRuleCategory.SalesBased) {
      // sales_basis is required for sales-based (hidden, reserved for future use)
      this.ruleForm.get('sales_basis')?.setValidators([Validators.required]);
    }
    this.ruleForm.get('sales_basis')?.updateValueAndValidity();

    if (!this.isEditMode) {
      this.ruleForm.patchValue({ scope_type: null });
      this.slabColumns = [];
      this.slabRows = [];
    }
    // Clear selected services since the filtered list changes per category
    this.ruleForm.patchValue({ scope_services: [] });
    this.serviceCategories = [];
    this.lastServiceCategoriesFetchKey = null;
    if (category) {
      this.ruleForm.get('scope_type')?.enable();
    } else {
      this.ruleForm.get('scope_type')?.disable();
    }
    this.changeDetectorRef.detectChanges();
  }

  onScopeTypeChange() {
    if (!this.isEditMode || !this.rule) {
      this.ruleForm.patchValue({ 
        scope_plans: [],
        scope_services: [],
        scope_products: [],
        scope_skus: [],
        limit_specific_skus: false
      });
    }

    const scopeType = this.ruleForm.get('scope_type')?.value;
    if (scopeType === CommissionScopeType.SubscriptionPlan) {
      this.loadSubscriptionPlans();
    } else if (scopeType === CommissionScopeType.ServiceCategory) {
      this.loadServiceCategories();
    } else if (scopeType === CommissionScopeType.RetailItem) {
      this.loadRetailProducts();
    }

    // Rebuild slab columns immediately (handles 'All' scope which has no multiselect)
    this.slabColumns = [];
    this.slabRows = [];
    this.rebuildSlabColumns();
    this.changeDetectorRef.detectChanges();
  }

  onScopeSelectionChange() {
    this.rebuildSlabColumns();
    this.changeDetectorRef.detectChanges();
  }

  rebuildSlabColumns() {
    const scopeType = this.ruleForm.get('scope_type')?.value;
    let newColumns: string[] = [];

    if (scopeType === CommissionScopeType.All) {
      newColumns = ['ALL'];
    } else if (scopeType === CommissionScopeType.SubscriptionPlan) {
      const selected: string[] = this.ruleForm.get('scope_plans')?.value || [];
      newColumns = selected.length > 0 ? selected : [];
    } else if (scopeType === CommissionScopeType.ServiceCategory) {
      const selected: string[] = this.ruleForm.get('scope_services')?.value || [];
      newColumns = selected.length > 0 ? selected : [];
    } else if (scopeType === CommissionScopeType.RetailItem) {
      const selectedIds: number[] = this.ruleForm.get('scope_products')?.value || [];
      newColumns = selectedIds.map(id => {
        const product = this.retailProducts.find(p => p.id === id);
        return product ? product.name : `Product ${id}`;
      });
    }

    // Preserve existing row values for columns that still exist
    const prevColumns = this.slabColumns;
    this.slabColumns = newColumns;

    if (newColumns.length > 0 && this.slabRows.length === 0) {
      this.addSlabRow();
    } else {
      // Add new columns to existing rows, remove old ones
      this.slabRows = this.slabRows.map(row => {
        const newRow: SlabRow = {
          quantity_from: row['quantity_from'] as number | null,
          quantity_to: row['quantity_to'] as number | null
        };
        newColumns.forEach(col => {
          newRow[col] = prevColumns.includes(col) ? (row[col] as number | null) : null;
        });
        return newRow;
      });
    }
  }

  addSlabRow() {
    const newRow: SlabRow = { quantity_from: null, quantity_to: null };
    this.slabColumns.forEach(col => { newRow[col] = null; });
    this.slabRows = [...this.slabRows, newRow];
    this.changeDetectorRef.detectChanges();
  }

  removeSlabRow(index: number) {
    this.slabRows = this.slabRows.filter((_, i) => i !== index);
    this.changeDetectorRef.detectChanges();
  }

  onRateTypeToggleChange() {
    this.changeDetectorRef.detectChanges();
  }

  onLimitSkusChange() {
    const limitSkus = this.ruleForm.get('limit_specific_skus')?.value;
    if (limitSkus && this.selectedProductIds.length > 0) {
      this.loadProductSkus();
    } else {
      this.productSkus = [];
      this.ruleForm.patchValue({ scope_skus: [] });
    }
    this.changeDetectorRef.detectChanges();
  }

  async loadSubscriptionPlans() {
    const fetchKey = `sub|${this.selectedOwnershipLevel}|fg=${this.tenantFranchiseId}|t=${this.tenantId}`;
    if (this.lastSubscriptionPlansFetchKey === fetchKey && this.subscriptionPlans.length > 0) {
      return;
    }
    this.loadingPlans = true;
    this.changeDetectorRef.detectChanges();

    try {
      if (this.selectedOwnershipLevel === OwnershipLevel.Company) {
        const res: { items?: { plan_name?: string; id?: number; plan_id?: number; name_primary_language?: string }[] } =
          await firstValueFrom(this.planmasterService.getCompanyLevelPlans().pipe(take(1)));
        const plans = res.items || [];
        const formattedPlans = plans.map((p, index: number) => ({
          id: p.id ?? p.plan_id ?? index,
          name: p.plan_name?.trim() || p.name_primary_language || `Plan ${p.id ?? p.plan_id ?? index}`
        }));
        this.subscriptionPlans = this.getDistinctByKey(formattedPlans, 'name');
        this.lastSubscriptionPlansFetchKey = fetchKey;
      } else if (this.selectedOwnershipLevel === OwnershipLevel.FranchiseGroup) {
        if (!this.tenantFranchiseId) {
          this.subscriptionPlans = [];
          return;
        }
        const res = await firstValueFrom(
          this.planmasterService.getFranchiseGroupSubscriptionPlans(this.tenantFranchiseId).pipe(take(1))
        );
        const plans = res.items || [];
        const formattedPlans = plans.map((p, index: number) => ({
          id: index,
          name: p.plan_name?.trim() || `Plan ${index + 1}`
        }));
        this.subscriptionPlans = this.getDistinctByKey(formattedPlans, 'name');
        this.lastSubscriptionPlansFetchKey = fetchKey;
      } else {
        const tenantIdsToFetch = [this.tenantId];

        const allPlans: { id: number; name: string }[] = [];
        const promises = tenantIdsToFetch.map(tid =>
          firstValueFrom(this.subscriptionService.getSubscriptionPlans(tid, []).pipe(take(1)))
            .then((subRes: { items?: Record<string, unknown>[] }) => {
              if (subRes.items) {
                allPlans.push(
                  ...subRes.items
                    .filter(
                      (plan: Record<string, unknown>) =>
                        plan['status'] == Status.Active.toString() || plan['status'] == Status[Status.Active]
                    )
                    .map((plan: Record<string, unknown>) => ({
                      id: plan['id'] as number,
                      name:
                        (plan['name_primary_language'] as string) ||
                        (plan['name_secondary_language'] as string) ||
                        (plan['name'] as string) ||
                        `Plan ${plan['id']}`
                    }))
                );
              }
            })
            .catch(() => undefined)
        );

        await Promise.all(promises);
        this.subscriptionPlans = this.getDistinctByKey(allPlans, 'name');
        this.lastSubscriptionPlansFetchKey = fetchKey;
      }
    } catch (error) {
      this.toastService.showError(error);
    } finally {
      this.loadingPlans = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  async loadServiceCategories() {
    const ruleCategory = this.ruleForm.get('category')?.value as CommissionRuleCategory | undefined;
    const behaviourParam = this.getServiceCategoriesBehaviourQueryParam(ruleCategory);
    const fetchKey = `svc|${this.selectedOwnershipLevel}|fg=${this.tenantFranchiseId}|t=${this.tenantId}|b=${behaviourParam ?? 'none'}`;
    if (this.lastServiceCategoriesFetchKey === fetchKey && this.serviceCategories.length > 0) {
      return;
    }
    this.loadingServices = true;
    this.changeDetectorRef.detectChanges();

    try {
      if (this.selectedOwnershipLevel === OwnershipLevel.Company) {
        if (behaviourParam) {
          const res = await firstValueFrom(
            this.serviceCategoryService.getCompanyServiceCategories(behaviourParam).pipe(take(1))
          );
          const categories = Array.isArray(res) ? res : [];
          const formattedCats = categories.map((c) => ({
            id: 0,
            name: c.category_name?.trim() || '',
            behaviour: this.mapCategoryBehaviourFromApi(c.category_behaviour)
          }));
          this.serviceCategories = this.getDistinctByKey(
            formattedCats.filter((c) => c.name.length > 0),
            'name'
          );
        } else {
          const res: unknown = await firstValueFrom(
            this.serviceCategoryService.getAggregatedServiceCategories().pipe(take(1))
          );
          const categories = (Array.isArray(res) ? res : []) as Record<string, unknown>[];
          const formattedCats = categories.map((c) => ({
            id: (c['category_id'] as number) || (c['id'] as number) || 0,
            name:
              (c['category_name'] as string) ||
              (c['name_primary_language'] as string) ||
              `Service ${(c['category_id'] as number) || (c['id'] as number)}`,
            behaviour: (c['behaviour'] as ServiceBehaviour) || ServiceBehaviour.AppointmentPackage
          }));
          this.serviceCategories = this.getDistinctByKey(formattedCats, 'name');
        }
        this.lastServiceCategoriesFetchKey = fetchKey;
      } else if (this.selectedOwnershipLevel === OwnershipLevel.FranchiseGroup) {
        if (behaviourParam && this.tenantFranchiseId) {
          const res = await firstValueFrom(
            this.serviceCategoryService
              .getFranchiseGroupServiceCategories(this.tenantFranchiseId, behaviourParam)
              .pipe(take(1))
          );
          const categories = Array.isArray(res) ? res : [];
          const formattedCats = categories.map((c) => ({
            id: 0,
            name: c.category_name?.trim() || '',
            behaviour: this.mapCategoryBehaviourFromApi(c.category_behaviour)
          }));
          this.serviceCategories = this.getDistinctByKey(
            formattedCats.filter((c) => c.name.length > 0),
            'name'
          );
        } else {
          const tenantIdsToFetch = this.tenantsList
            .filter((t) => t.franchise_group_id === this.tenantFranchiseId)
            .map((t) => t.id);

          const allCategories: { id: number; name: string; behaviour: ServiceBehaviour }[] = [];
          const promises = tenantIdsToFetch.map((tid) => {
            if (tid === this.tenantId) {
              return firstValueFrom(this.store.select(SERVICETYPE_LIST_STATE).pipe(take(1)))
                .then((svcRes) => {
                  const svcs = (svcRes ?? []) as ServiceTypeResponse[];
                  allCategories.push(...svcs.map((s) => this.mapServiceTypeToCategoryOption(s)));
                })
                .catch(() => undefined);
            }
            return firstValueFrom(this.serviceCategoryService.getServiceCategories(tid).pipe(take(1)))
              .then((catRes: unknown) => {
                const raw = catRes as { items?: unknown[] } | unknown[];
                const items = Array.isArray(raw) ? raw : (raw.items ?? []);
                const list = Array.isArray(items) ? items : [];
                allCategories.push(...list.map((row) => this.mapTenantCategoryApiRowToOption(row)));
              })
              .catch(() => undefined);
          });
          await Promise.all(promises);
          this.serviceCategories = this.getDistinctByKey(allCategories, 'name');
        }
        this.lastServiceCategoriesFetchKey = fetchKey;
      } else {
        const res = await firstValueFrom(this.store.select(SERVICETYPE_LIST_STATE).pipe(take(1)));
        const svcs = (res ?? []) as ServiceTypeResponse[];
        this.serviceCategories = this.getDistinctByKey(
          svcs.map((s) => this.mapServiceTypeToCategoryOption(s)),
          'name'
        );
        this.lastServiceCategoriesFetchKey = fetchKey;
      }
    } catch (error) {
      this.toastService.showError(error);
    } finally {
      this.loadingServices = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  private getServiceCategoriesBehaviourQueryParam(
    category: CommissionRuleCategory | null | undefined
  ): string | null {
    if (category === CommissionRuleCategory.AppointmentUsageBased) {
      return 'AppointmentPackage';
    }
    if (category === CommissionRuleCategory.ClassUsageBased) {
      return 'ClassPackage';
    }
    return null;
  }

  private mapCategoryBehaviourFromApi(raw: string | undefined): ServiceBehaviour {
    if (!raw) {
      return ServiceBehaviour.AppointmentPackage;
    }
    const normalized = raw.replace(/\s/g, '').toLowerCase();
    if (normalized.includes('class')) {
      return ServiceBehaviour.ClassPackage;
    }
    if (normalized.includes('appointment')) {
      return ServiceBehaviour.AppointmentPackage;
    }
    if (normalized.includes('member')) {
      return ServiceBehaviour.Membership;
    }
    return ServiceBehaviour.AppointmentPackage;
  }

  private mapServiceTypeToCategoryOption(s: ServiceTypeResponse): {
    id: number;
    name: string;
    behaviour: ServiceBehaviour;
  } {
    return {
      id: s.id,
      name: s.name_primary_language || s.name_secondary_language || `Service ${s.id}`,
      behaviour: s.behaviour
    };
  }

  private mapTenantCategoryApiRowToOption(row: unknown): {
    id: number;
    name: string;
    behaviour: ServiceBehaviour;
  } {
    const s = row as {
      id?: number;
      name_primary_language?: string;
      name_secondary_language?: string;
      name?: string;
      behaviour?: ServiceBehaviour;
    };
    const id = s.id ?? 0;
    return {
      id,
      name: s.name_primary_language || s.name_secondary_language || s.name || `Service ${id}`,
      behaviour: s.behaviour ?? ServiceBehaviour.AppointmentPackage
    };
  }

  private getDistinctByKey(array: any[], key: string) {
    return Array.from(new Map(array.map(item => [item[key], item])).values());
  }

  loadRetailProducts() {
    if (this.retailProducts.length > 0) return;
    this.loadingProducts = true;
    const requestParams = new Map<string, any>();
    requestParams.set('PageNumber', { name: 'PageNumber', value: 1 });
    requestParams.set('PageSize', { name: 'PageSize', value: 1000 });

    this.productService.getTenantProducts(this.tenantId, requestParams).subscribe({
      next: (response: any) => {
        this.retailProducts = (response.items || response.data || []).map((product: any) => ({
          id: product.id,
          name: product.name_primary_language || product.name_secondary_language || product.name || `Product ${product.id}`
        }));
        this.loadingProducts = false;
        this.changeDetectorRef.detectChanges();
      },
      error: (error) => {
        this.toastService.showError(error);
        this.loadingProducts = false;
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  loadProductSkus() {
    if (this.selectedProductIds.length === 0) return;
    if (this.isLoadingSkus) return;

    this.isLoadingSkus = true;
    this.loadingSkus = true;
    this.productSkus = [];

    const cachedSkus: { sku_name: string; name: string; product_name: string; product_id: number }[] = [];
    const productsToLoad: number[] = [];

    this.selectedProductIds.forEach(productId => {
      const cached = this.skuCache.get(productId);
      if (cached) {
        cachedSkus.push(...cached);
      } else {
        productsToLoad.push(productId);
      }
    });

    this.productSkus.push(...cachedSkus);

    if (productsToLoad.length === 0) {
      this.loadingSkus = false;
      this.isLoadingSkus = false;
      this.cleanupSelectedSkus();
      this.changeDetectorRef.detectChanges();
      return;
    }

    const skuPromises = productsToLoad.map(productId => {
      const requestParams = new Map<string, any>();
      requestParams.set('PageNumber', { name: 'PageNumber', value: 1 });
      requestParams.set('PageSize', { name: 'PageSize', value: 1000 });
      return this.productSkuService.getCompanyProductSkus(productId, requestParams).toPromise();
    });

    Promise.all(skuPromises).then((responses: any[]) => {
      responses.forEach((response, index) => {
        const productId = productsToLoad[index];
        const productName = this.retailProducts.find(p => p.id === productId)?.name || `Product ${productId}`;
        const skus = (response?.items || response?.data || []).map((sku: any) => ({
          sku_name: sku.sku_name || sku.name || `SKU ${sku.id}`,
          name: sku.sku_name || sku.name || `SKU ${sku.id}`,
          product_name: productName,
          product_id: productId
        }));
        this.skuCache.set(productId, skus);
        this.productSkus.push(...skus);
      });
      this.loadingSkus = false;
      this.isLoadingSkus = false;
      this.cleanupSelectedSkus();
      this.changeDetectorRef.detectChanges();
    }).catch((error) => {
      this.toastService.showError(error);
      this.loadingSkus = false;
      this.isLoadingSkus = false;
      this.changeDetectorRef.detectChanges();
    });
  }

  cleanupSelectedSkus() {
    const currentSkus = this.ruleForm.get('scope_skus')?.value || [];
    if (currentSkus.length > 0 && this.productSkus.length > 0) {
      const validSkus = currentSkus.filter((skuName: string) => {
        const sku = this.productSkus.find(s => s.sku_name === skuName);
        return sku && this.selectedProductIds.includes(sku.product_id);
      });
      if (validSkus.length !== currentSkus.length) {
        this.ruleForm.patchValue({ scope_skus: validSkus }, { emitEvent: false });
      }
    }
  }

  loadRule() {
    if (this.ruleId) {
      this.commissionPlanRuleService.getCommissionPlanRule(this.planId, this.ruleId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (rule: CommissionPlanRuleResponse) => {
            this.rule = rule;
            this.populateForm();
            this.changeDetectorRef.detectChanges();
          },
          error: (error) => {
            this.toastService.showError(error);
            this.changeDetectorRef.detectChanges();
          }
        });
    }
  }

  populateForm() {
    if (!this.rule) return;

    const scopeName = this.rule.scope_name || '';
    const scopeSkuName = this.rule.scope_sku_name || '';

    let scopePlans: string[] = [];
    let scopeServices: string[] = [];
    let scopeProducts: string[] = [];
    let scopeSkus: string[] = [];
    let limitSpecificSkus = false;

    if (this.rule.scope_type === CommissionScopeType.SubscriptionPlan && scopeName) {
      scopePlans = scopeName.split(',').map(n => n.trim());
    } else if (this.rule.scope_type === CommissionScopeType.ServiceCategory && scopeName) {
      scopeServices = scopeName.split(',').map(n => n.trim());
    } else if (this.rule.scope_type === CommissionScopeType.RetailItem && scopeName) {
      scopeProducts = scopeName.split(',').map(n => n.trim());
      if (scopeSkuName && scopeSkuName !== 'all') {
        limitSpecificSkus = true;
        scopeSkus = scopeSkuName.split(',').map(s => s.trim());
      }
    }

    // Set rate type toggle from saved rule
    this.isPercentageRate = this.rule.rate_type !== CommissionRateType.FlatFee;

    this.ruleForm.patchValue({
      category: this.rule.category,
      sales_basis: this.rule.sales_basis,
      scope_type: this.rule.scope_type,
      limit_specific_skus: limitSpecificSkus,
      flat_trial_value: this.rule.flat_trial_value ? this.commonService.ConvertAmountFromMinorCurrency(this.rule.flat_trial_value) : null,
      flat_free_value: this.rule.flat_free_value ? this.commonService.ConvertAmountFromMinorCurrency(this.rule.flat_free_value) : null,
      flat_comp_value: this.rule.flat_comp_value ? this.commonService.ConvertAmountFromMinorCurrency(this.rule.flat_comp_value) : null,
      noshow_value_in_percent: this.rule.noshow_value_in_percent
    });

    // Enable scope_type (disabled by default until category is chosen)
    this.ruleForm.get('scope_type')?.enable();

    // Populate slab table from API response
    this.populateSlabTable();

    if (this.rule.scope_type === CommissionScopeType.All) {
      // No data to load, columns are just ['ALL']
    } else if (this.rule.scope_type === CommissionScopeType.SubscriptionPlan) {
      this.loadSubscriptionPlans();
      setTimeout(() => {
        this.ruleForm.patchValue({ scope_plans: scopePlans });
        this.changeDetectorRef.detectChanges();
      }, 500);
    } else if (this.rule.scope_type === CommissionScopeType.ServiceCategory) {
      this.loadServiceCategories();
      setTimeout(() => {
        this.ruleForm.patchValue({ scope_services: scopeServices });
        this.changeDetectorRef.detectChanges();
      }, 500);
    } else if (this.rule.scope_type === CommissionScopeType.RetailItem) {
      this.loadRetailProducts();
      setTimeout(() => {
        const productIds = this.retailProducts
          .filter(p => scopeProducts.includes(p.name))
          .map(p => p.id);
        this.ruleForm.patchValue({ scope_products: productIds });
        if (scopeProducts.length > 0 && limitSpecificSkus) {
          setTimeout(() => {
            this.selectedProductIds = productIds;
            this.loadProductSkus();
            setTimeout(() => {
              this.ruleForm.patchValue({ scope_skus: scopeSkus });
              this.changeDetectorRef.detectChanges();
            }, 500);
          }, 500);
        }
        this.changeDetectorRef.detectChanges();
      }, 500);
    }
  }

  populateSlabTable() {
    if (!this.rule?.slabs?.length) return;

    // Derive columns from the first slab (sorted asc by id) values (dimensionId keys)
    const sortedSlabs = [...this.rule.slabs].sort((a, b) => a.id - b.id);
    const firstSlab = sortedSlabs[0];
    if (firstSlab.values?.length) {
      this.slabColumns = firstSlab.values.map(v => v.dimensionId);
    } else if (this.rule.scope_type === CommissionScopeType.All) {
      this.slabColumns = ['ALL'];
    } else {
      // Fallback: derive columns from scope_name
      const scopeName = this.rule.scope_name || '';
      this.slabColumns = scopeName ? scopeName.split(',').map(n => n.trim()) : [];
    }

    // Build rows from slabs sorted by id asc — carry the slab id so edits can update vs create
    this.slabRows = sortedSlabs.map(slab => {
      const row: SlabRow = {
        slabId: slab.id,
        quantity_from: slab.quantity_from ?? null,
        quantity_to: slab.quantity_to ?? null
      };
      this.slabColumns.forEach(col => { row[col] = null; });
      (slab.values || []).forEach(v => {
        // API stores percentage as the display value (e.g. 10 = 10%), use it directly
        if (v.percentage !== null && v.percentage !== undefined) {
          row[v.dimensionId] = parseFloat(v.percentage.toFixed(2));
        } else if (v.amountMinorCurr !== null && v.amountMinorCurr !== undefined) {
          row[v.dimensionId] = this.commonService.ConvertAmountFromMinorCurrency(v.amountMinorCurr);
        }
      });
      return row;
    });

    this.changeDetectorRef.detectChanges();
  }

  buildSlabRequests(): CreateCommissionPlanSlabRequest[] {
    if (this.slabRows.length === 0 || this.slabColumns.length === 0) {
      return [];
    }

    const rateType = this.isPercentageRate ? CommissionRateType.Percentage : CommissionRateType.FlatFee;

    return this.slabRows.map(row => {
      const values: CommissionSlabDimensionValue[] = this.slabColumns
        .filter(col => row[col] !== null && row[col] !== undefined)
        .map(col => {
          const dimValue: CommissionSlabDimensionValue = { dimension_id: col };
          if (rateType === CommissionRateType.Percentage) {
            dimValue.percentage = row[col] as number;
          } else {
            dimValue.amount_minor_curr = this.commonService.ConvertMinorCurrencyFromAmount(row[col] as number);
          }
          return dimValue;
        });

      return {
        quantity_from: (row['quantity_from'] as number) ?? 0,
        quantity_to: (row['quantity_to'] as number) ?? 0,
        values
      };
    });
  }

  /**
   * After a rule update, sync the slab table:
   *  - rows with a slabId  → PUT (update)
   *  - rows without a slabId → POST (create)
   *  - original slabs whose id is no longer in the table → DELETE (deactivate)
   */
  private syncSlabs(): Observable<unknown> {
    const slabRequests = this.buildSlabRequests();
    const originalSlabIds = (this.rule?.slabs ?? []).map(s => s.id);
    const currentSlabIds = this.slabRows
      .map(r => r['slabId'] as number | undefined)
      .filter((id): id is number => id !== undefined);

    const calls: Observable<unknown>[] = [];

    // Deactivate slabs that were removed from the table
    const removedIds = originalSlabIds.filter(id => !currentSlabIds.includes(id));
    removedIds.forEach(slabId => {
      calls.push(
        this.commissionPlanSlabService.deactivateCommissionPlanSlab(this.planId, this.ruleId!, slabId)
      );
    });

    // Update or create each row
    slabRequests.forEach((slabReq, index) => {
      const row = this.slabRows[index];
      const slabId = row['slabId'] as number | undefined;
      if (slabId) {
        calls.push(
          this.commissionPlanSlabService.updateCommissionPlanSlab(this.planId, this.ruleId!, slabId, slabReq)
        );
      } else {
        calls.push(
          this.commissionPlanSlabService.createCommissionPlanSlab(this.planId, this.ruleId!, slabReq)
        );
      }
    });

    return calls.length > 0 ? forkJoin(calls) : of(null);
  }

  saveRule() {
    if (this.ruleForm.valid) {
      this.saving = true;
      const formData = this.ruleForm.value;

      let scopeName: string | undefined;
      let scopeSkuName: string | undefined;

      const scopeType = formData.scope_type;

      if (scopeType === CommissionScopeType.All) {
        scopeName = undefined;
        scopeSkuName = undefined;
      } else if (scopeType === CommissionScopeType.SubscriptionPlan) {
        scopeName = formData.scope_plans && formData.scope_plans.length > 0 
          ? formData.scope_plans.join(',') 
          : undefined;
      } else if (scopeType === CommissionScopeType.ServiceCategory) {
        scopeName = formData.scope_services && formData.scope_services.length > 0 
          ? formData.scope_services.join(',') 
          : undefined;
      } else if (scopeType === CommissionScopeType.RetailItem) {
        if (formData.scope_products && formData.scope_products.length > 0) {
          const productNames = formData.scope_products.map((productId: number) => {
            const product = this.retailProducts.find(p => p.id === productId);
            return product ? product.name : productId.toString();
          });
          scopeName = productNames.join(',');
        } else {
          scopeName = undefined;
        }
        if (formData.limit_specific_skus && formData.scope_skus && formData.scope_skus.length > 0) {
          scopeSkuName = formData.scope_skus.join(',');
        } else {
          scopeSkuName = 'all';
        }
      }

      const rateType = this.isPercentageRate ? CommissionRateType.Percentage : CommissionRateType.FlatFee;

      const request: CreateCommissionPlanRuleRequest | UpdateCommissionPlanRuleRequest = {
        category: formData.category,
        sales_basis: formData.sales_basis || CommissionSalesBasis.SessionCount,
        scope_type: formData.scope_type,
        scope_name: scopeName,
        scope_sku_name: scopeSkuName,
        is_cumulative: false,
        flat_trial_value: formData.flat_trial_value ? this.commonService.ConvertMinorCurrencyFromAmount(formData.flat_trial_value) : undefined,
        flat_free_value: formData.flat_free_value ? this.commonService.ConvertMinorCurrencyFromAmount(formData.flat_free_value) : undefined,
        flat_comp_value: formData.flat_comp_value ? this.commonService.ConvertMinorCurrencyFromAmount(formData.flat_comp_value) : undefined,
        noshow_value_in_percent: formData.noshow_value_in_percent,
        rate_type: rateType,
        slabs: []
      };

      if (this.isEditMode && this.ruleId) {
        this.commissionPlanRuleService.updateCommissionPlanRule(this.planId, this.ruleId, request as UpdateCommissionPlanRuleRequest).pipe(
          // After rule update succeeds, sync slabs (update/create/deactivate)
          take(1)
        ).subscribe({
          next: () => {
            this.syncSlabs().pipe(take(1)).subscribe({
              next: () => {
                this.saving = false;
                this.toastService.showSuccess(this.translate.instant('COMMISSION_MANAGEMENT.RULES.UPDATE_SUCCESS'));
                this.changeDetectorRef.detectChanges();
                this.goBack();
              },
              error: (error: unknown) => {
                this.saving = false;
                this.toastService.showError(error);
                this.changeDetectorRef.detectChanges();
              }
            });
          },
          error: (error: unknown) => {
            this.saving = false;
            this.toastService.showError(error);
            this.changeDetectorRef.detectChanges();
          }
        });
      } else {
        request.slabs = this.buildSlabRequests();
        this.commissionPlanRuleService.createCommissionPlanRule(this.planId, request as CreateCommissionPlanRuleRequest).subscribe({
          next: () => {
            this.saving = false;
            this.toastService.showSuccess(this.translate.instant('COMMISSION_MANAGEMENT.RULES.CREATE_SUCCESS'));
            this.changeDetectorRef.detectChanges();
            this.goBack();
          },
          error: (error: unknown) => {
            this.saving = false;
            this.toastService.showError(error);
            this.changeDetectorRef.detectChanges();
          }
        });
      }
    }
  }

  goBack() {
    this.router.navigate([
      '/admin/commission-management/plans',
      this.selectedOwnershipLevel,
      this.planId,
      'rules'
    ]);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
